import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  Textarea,
  Select,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import type { RoleType, TenantSettings } from '@isg/shared-types';
import { ROLE_TYPES } from '@isg/shared-types';
import {
  KPI_CARPANLARI,
  KPI_CARPAN_ETIKETI,
  RISK_METODOLOJI_ETIKETI,
  RISK_METODOLOJILERI,
  RISK_SEVIYELERI,
  RISK_SEVIYESI_ETIKETI,
  TENANT_ANAHTAR_ETIKETLERI,
  TENANT_KAPI_ANAHTARLARI,
  TENANT_LISTE_ANAHTARLARI,
  TENANT_YETKI_ANAHTARLARI,
} from '@isg/validation';
import type { TenantSettingAnahtar } from '@isg/validation';
import {
  useEkipmanIsyeriPeriyoduYaz,
  useEkipmanPeriyotConfig,
  useRiskBantTablosu,
  useRiskBantYaz,
  useTenantSettingYaz,
  useTenantSettings,
} from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';
import { QueryStatus } from './parts';
import { AyarDefteri, PtwKontrolOzetiSatirlari } from './kontrol-matrisi-parts';
import type { EkipmanPeriyotConfig, EkipmanTipPeriyodu } from '@isg/validation';
import { ANLAM } from '../theme';

/**
 * YAPILANDIRMA EKRANI (F4·S2 — Kapsam §23; tutanak
 * `yapilandirma-ekrani-kararlari-v1_0.md` Y1-Y7 · Z1-Z6).
 *
 * Beş modüle dağılmış **14 tenant anahtarı** tek yerde. Yeni mekanizma
 * yoktur: hepsi mevcut `tenant_setting` ailesindedir ve TEK uçtan yazılır
 * (`PUT /api/tenant-settings/:anahtar`, yalnız `holding_admin`).
 *
 * ⚠️ **ETİKETLER TEK YERDE** (`@isg/validation TENANT_ANAHTAR_ETIKETLERI`) ve
 * ürün sahibi onaylıdır (madde 65). Buraya dağınık string yazma — panel yorum
 * yapmadığına göre (Y4) kapalı bir kontrolü anlatan **tek şey adıdır**.
 * **TEKNİK ADLAR KULLANICIYA GÖSTERİLMEZ.**
 *
 * ⚠️ **YETKİ KARARI SUNUCUDA** (§2.16): menü görünürlüğü yalnız görünümü
 * sadeleştirir; holding yöneticisi olmayanın PUT'u zaten 403'ler.
 */
export function YapilandirmaScreen(): React.JSX.Element {
  const ayarlar = useTenantSettings();
  return (
    <Stack maw={900}>
      <Title order={3}>Yapılandırma</Title>
      <Text size="sm" c="dimmed">
        Bu ayarlar holdingin tamamı için geçerlidir; şirket bazında değiştirilemez.
      </Text>
      <QueryStatus queries={[ayarlar]} />
      {ayarlar.isSuccess && (
        <>
          <AktifOlmayanlar ayarlar={ayarlar.data} />
          <KapiAnahtarlari ayarlar={ayarlar.data} />
          <YetkiAnahtarlari ayarlar={ayarlar.data} />
          <ListeAnahtarlari ayarlar={ayarlar.data} />
          <DegerAnahtarlari ayarlar={ayarlar.data} />
          <EkipmanPeriyotlari />
          <AyarDefteri anahtar={null} baslik="Değişiklik defteri" testId="ayar-defteri" />
        </>
      )}
    </Stack>
  );
}

/**
 * "AKTİF OLMAYAN KONTROLLER" (Y4 + Y7) — **DÜZ LİSTE: etiket + "kapalı".**
 * Yorum yok, etki cümlesi yok, öneri yok, uyarı rengi yok: bir kontrolü kapalı
 * tutmak çoğu zaman bilinçli tercihtir ve ürün mevzuat tavsiyesi verir konuma
 * geçmez (Kapsam §23/6).
 *
 * Liste SUNUCUDAN gelir (`kapaliKontroller`) — ekran "hangi anahtar panele
 * girer" kuralını İKİNCİ KEZ yazmaz. Dördü de açıksa panel **boş durumunu
 * gösterir, gizlenmez.**
 */
function AktifOlmayanlar({ ayarlar }: { ayarlar: TenantSettings }): React.JSX.Element {
  return (
    <Card withBorder data-testid="aktif-olmayan-kontroller">
      <Stack gap="xs">
        <Text fw={600}>Aktif olmayan kontroller</Text>
        {ayarlar.kapaliKontroller.length === 0 ? (
          <Text size="sm" c="dimmed" data-testid="aktif-olmayan-bos">
            Kapalı kontrol yok.
          </Text>
        ) : (
          ayarlar.kapaliKontroller.map((k) => (
            <Text key={k.anahtar} size="sm" data-testid="aktif-olmayan-satir">
              {k.etiket} — kapalı
            </Text>
          ))
        )}
        {/* ✏️ DİLİM 2 (bulgu 75): iş izni muslukları — anahtar eklenmez,
            OLMAYAN SAYILIR; sayılar sunucudan (tek çözümleyici). */}
        <PtwKontrolOzetiSatirlari ozet={ayarlar.ptwKontrolOzeti} />
      </Stack>
    </Card>
  );
}

/** Kapı anahtarları (5) — etiket açıkken ne olduğunu söyleyen CÜMLEDİR.
 *  ✏️ Tur 4½: `gorevlendirme_yetkinlik_zorunlu` eklendi (§25/5). */
function KapiAnahtarlari({ ayarlar }: { ayarlar: TenantSettings }): React.JSX.Element {
  const yaz = useTenantSettingYaz();
  // ✏️ DİLİM 2 (bulgu 76): bu kartta YALNIZ kontrol sınıfı kalır.
  const deger: Record<(typeof TENANT_KAPI_ANAHTARLARI)[number], boolean> = {
    risk_aksiyon_zorunlu: ayarlar.riskAksiyonZorunlu,
    gorevlendirme_yetkinlik_zorunlu: ayarlar.gorevlendirmeYetkinlikZorunlu,
  };
  return (
    <Card withBorder data-testid="kapi-anahtarlari">
      <Stack gap="sm">
        <Text fw={600}>Kontroller</Text>
        {TENANT_KAPI_ANAHTARLARI.map((anahtar) => (
          <Switch
            key={anahtar}
            data-testid={`ayar-${anahtar}`}
            label={TENANT_ANAHTAR_ETIKETLERI[anahtar]}
            checked={deger[anahtar]}
            onChange={(event) => yaz.mutate({ anahtar, deger: event.currentTarget.checked })}
          />
        ))}
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/**
 * ✏️ DİLİM 2 (15.09.2026 — bulgu 76; Kapsam §23/2a ✏️): YETKİ ANAHTARLARI.
 * Bir kontrolü değil bir YETKİYİ ya da KANALI açarlar; bu yüzden "Aktif
 * olmayan kontroller" panelinde sayılmazlar. Yazma yolu aynı uç.
 */
function YetkiAnahtarlari({ ayarlar }: { ayarlar: TenantSettings }): React.JSX.Element {
  const yaz = useTenantSettingYaz();
  const deger: Record<(typeof TENANT_YETKI_ANAHTARLARI)[number], boolean> = {
    uzman_aksiyon_atama: ayarlar.uzmanAksiyonAtama,
    anonim_bildirim_acik: ayarlar.anonimBildirimAcik,
    egitim_degerlendirme_acik: ayarlar.egitimDegerlendirmeAcik,
  };
  return (
    <Card withBorder data-testid="yetki-anahtarlari">
      <Stack gap="sm">
        <Text fw={600}>Yetki anahtarları</Text>
        {TENANT_YETKI_ANAHTARLARI.map((anahtar) => (
          <Switch
            key={anahtar}
            data-testid={`ayar-${anahtar}`}
            label={TENANT_ANAHTAR_ETIKETLERI[anahtar]}
            checked={deger[anahtar]}
            onChange={(event) => yaz.mutate({ anahtar, deger: event.currentTarget.checked })}
          />
        ))}
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/**
 * Liste anahtarları (5) — etiket "…-ebilenler" biçimindedir (✏️ T2·0:
 * `olay_kapanis` kütükten düştü — kapanış türetilir).
 * **BOŞ LİSTE = VARSAYILAN GEÇERLİ** (c1): kaydedilen boş liste kontrolü
 * kapatmaz, ürün varsayılanına döndürür — bu yüzden panelde de görünmezler.
 * Taban kilitler sunucudadır (izleyici hiçbir listeye, yüklenici temsilcisi
 * yalnız çalışma saatine) ve ihlal 422 döner; ekran ikinci kural yazmaz.
 */
function ListeAnahtarlari({ ayarlar }: { ayarlar: TenantSettings }): React.JSX.Element {
  const yaz = useTenantSettingYaz();
  const [taslak, setTaslak] = useState<Partial<Record<TenantSettingAnahtar, RoleType[]>>>({});
  const mevcut: Record<(typeof TENANT_LISTE_ANAHTARLARI)[number], RoleType[]> = {
    olay_inceleme: ayarlar.olayInceleme,
    calisma_saati_giris: ayarlar.calismaSaatiGiris,
    egitim_kaydi: ayarlar.egitimKaydi,
    saglik_kanaat_giris: ayarlar.saglikKanaatGiris,
    risk_degerlendirme: ayarlar.riskDegerlendirme,
    uygunsuzluk_degerlendirme: ayarlar.uygunsuzlukDegerlendirme,
  };
  const secenekler = ROLE_TYPES.map((r) => ({ value: r, label: SAPKA_ETIKET[r] }));
  return (
    <Card withBorder data-testid="liste-anahtarlari">
      <Stack gap="sm">
        <Text fw={600}>Yetki listeleri</Text>
        <Text size="sm" c="dimmed">
          Liste boş bırakılırsa ürünün varsayılan şapka listesi geçerli olur.
        </Text>
        {TENANT_LISTE_ANAHTARLARI.map((anahtar) => (
          <Group key={anahtar} align="flex-end" gap="xs">
            <MultiSelect
              label={TENANT_ANAHTAR_ETIKETLERI[anahtar]}
              data={secenekler}
              value={taslak[anahtar] ?? mevcut[anahtar]}
              onChange={(v) => setTaslak((o) => ({ ...o, [anahtar]: v as RoleType[] }))}
              data-testid={`ayar-${anahtar}`}
              searchable
              style={{ flex: 1, minWidth: 360 }}
            />
            <Button
              variant="light"
              data-testid={`ayar-${anahtar}-kaydet`}
              loading={yaz.isPending}
              onClick={() =>
                yaz.mutate(
                  { anahtar, deger: taslak[anahtar] ?? mevcut[anahtar] },
                  { onSuccess: () => setTaslak((o) => ({ ...o, [anahtar]: undefined })) },
                )
              }
            >
              Kaydet
            </Button>
          </Group>
        ))}
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/**
 * Değer ve eşik anahtarları (4) — etiket isim tamlamasıdır. İkisi F4·S2'de
 * YENİ (Y1) ve **HOLDİNG DÜZEYİNDEDİR: şirkete inmez** (§23/3 — eşik her
 * yerde aynı olmazsa konsolide rapor elmayla armut toplar).
 *
 * ⚠️ **Z2:** eşik değişince eski kayıtların ROZETİ güncel eşikle yeniden
 * hesaplanır; **kapanmış kayıtların kapısı yeniden çalıştırılmaz.**
 */
function DegerAnahtarlari({ ayarlar }: { ayarlar: TenantSettings }): React.JSX.Element {
  const yaz = useTenantSettingYaz();
  const [majorEsik, setMajorEsik] = useState<number | null>(null);
  const [terminOnUyari, setTerminOnUyari] = useState<number | string>('');
  // KKD (Kapsam §30/Z3) — matbu metin taslağı; kaydet düğmesi metin değişince açılır.
  const [kkdMetin, setKkdMetin] = useState<string | null>(null);
  const kkdTaslak = kkdMetin ?? ayarlar.kkdZimmetMetni;
  const etiket = (a: TenantSettingAnahtar): string => TENANT_ANAHTAR_ETIKETLERI[a];
  // Değer anahtarları BEŞ tanedir ve her birinin girdi biçimi farklıdır
  // (kapalı liste · kapalı liste · 0-5 sıra · puan · gün), bu yüzden kapı/
  // liste bölümlerinin aksine tek tek çizilirler — döngüye sokmak farklı
  // girdiyi tek kalıba zorlardı.
  return (
    <Card withBorder data-testid="deger-anahtarlari">
      <Stack gap="sm">
        <Text fw={600}>Değerler ve eşikler</Text>
        <Select
          label={etiket('qr_ozet_kapsami')}
          data={[
            { value: 'sirket', label: 'İznin şirketindeki iç kullanıcılar' },
            { value: 'lokasyon', label: 'Yalnız iznin lokasyonunu kapsayanlar' },
          ]}
          value={ayarlar.qrOzetKapsami}
          data-testid="ayar-qr_ozet_kapsami"
          onChange={(v) => v && yaz.mutate({ anahtar: 'qr_ozet_kapsami', deger: v })}
        />
        <Select
          label={etiket('risk_metodolojisi')}
          data={[
            { value: 'matrix_5x5', label: '5x5 matris (olasılık × şiddet)' },
            { value: 'fine_kinney', label: 'Fine-Kinney (olasılık × frekans × şiddet)' },
          ]}
          value={ayarlar.riskMetodolojisi}
          data-testid="ayar-risk_metodolojisi"
          onChange={(v) => v && yaz.mutate({ anahtar: 'risk_metodolojisi', deger: v })}
        />
        {/* ✏️ **TUR 12 / K1 — DASHBOARD ORAN ÇARPANININ VARSAYILANI.**
            ⚠️ Bu bir KİLİT DEĞİLDİR: çarpan dashboard'da SÜZGEÇTİR ve
            kullanıcı her raporda değiştirebilir; burada seçilen değer yalnız
            ekranın AÇILIŞ DEĞERİDİR (Kapsam §26.1). Kilit olsaydı aynı dönem
            için iki farklı çarpanla EŞZAMANLI çıktı almak imkânsızlaşırdı
            (§16.8.1/2 — kararı doğuran ürün sahibi senaryosu). */}
        <Select
          label={etiket('kpi_carpan')}
          description="Gösterge panelinin açılışta kullandığı oran çarpanı; panelde her zaman değiştirilebilir."
          data={KPI_CARPANLARI.map((deger) => ({
            value: String(deger),
            label: KPI_CARPAN_ETIKETI[deger],
          }))}
          value={String(ayarlar.kpiCarpan)}
          data-testid="ayar-kpi_carpan"
          onChange={(v) => v && yaz.mutate({ anahtar: 'kpi_carpan', deger: Number(v) })}
        />
        {/* ✏️ **KKD / Z3 (Kapsam §30):** zimmet formunun ÜST bloğuna basılan
            matbu metin. Standart metin bizden (mevzuat atfı yok), müşteri
            değiştirir; boş yazılamaz (422 sunucuda). İlk serbest metin değer
            anahtarı — onun için Textarea + ayrı kaydet düğmesi. */}
        <Textarea
          label={etiket('kkd_zimmet_metni')}
          autosize
          minRows={3}
          value={kkdTaslak}
          data-testid="ayar-kkd_zimmet_metni"
          onChange={(e) => setKkdMetin(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button
            size="xs"
            variant="light"
            data-testid="ayar-kkd_zimmet_metni-kaydet"
            disabled={kkdTaslak.trim().length === 0 || kkdTaslak === ayarlar.kkdZimmetMetni}
            loading={yaz.isPending}
            onClick={() =>
              yaz.mutate(
                { anahtar: 'kkd_zimmet_metni', deger: kkdTaslak },
                { onSuccess: () => setKkdMetin(null) },
              )
            }
          >
            Metni kaydet
          </Button>
        </Group>
        <Alert variant="light" data-testid="metodoloji-notu">
          Yöntem değişikliği yalnız yeni açılan değerlendirmeleri etkiler; mevcut kayıtlar açılışta
          seçilen yöntemle okunmaya devam eder.
        </Alert>
        <Group align="flex-end" gap="xs">
          <NumberInput
            label={etiket('major_arastirma_esigi')}
            description="Potansiyel şiddet bu sıraya ulaşan kayıtlar majör araştırma işareti alır (0-5)."
            min={0}
            max={5}
            allowDecimal={false}
            value={majorEsik ?? ayarlar.majorArastirmaEsigi}
            onChange={(v) => setMajorEsik(typeof v === 'number' ? v : Number(v))}
            data-testid="ayar-major_arastirma_esigi"
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            loading={yaz.isPending}
            data-testid="ayar-major_arastirma_esigi-kaydet"
            onClick={() =>
              yaz.mutate(
                {
                  anahtar: 'major_arastirma_esigi',
                  deger: majorEsik ?? ayarlar.majorArastirmaEsigi,
                },
                { onSuccess: () => setMajorEsik(null) },
              )
            }
          >
            Kaydet
          </Button>
        </Group>
        {/* ✏️ TUR 11 / R2 — ELLE DÖF EŞİĞİ ALANI KALKTI. Eşik artık HER ZAMAN
            yüksek bandın alt sınırıdır ve düzenleme yeri BANT TABLOSUDUR
            (tek kaynak). Değer burada SALT OKUNUR gösterilir ki kullanıcı
            *"öneri neye göre çıkıyor"* sorusunu ekranı terk etmeden
            cevaplayabilsin. */}
        <Alert variant="light" data-testid="dof-esigi-notu">
          {ayarlar.riskDofOneriEsigiGecerli === null
            ? 'DÖF önerisi şu an üretilmiyor: bant sınırları tanımlanmamış.'
            : `DÖF önerisi ${ayarlar.riskDofOneriEsigiGecerli} puandan itibaren çıkar — ` +
              'bu değer aşağıdaki bant tablosunun "Yüksek" satırının alt sınırıdır. ' +
              'Değiştirmek için o satırı düzenleyin.'}
        </Alert>

        {/* R4-R5 — RİSK YENİLEME PERİYODU: müşteri SIKILAŞTIRIR, gevşetemez.
            Boş bırakmak bir eksiklik değil, "mevzuatın dediği gibi" demektir;
            tavanı aşan değer sunucuda 422 alır ve hata metni hangi sınıfta
            kaç ayı aştığını söyler. */}
        <RiskYenilemePeriyodu
          mevcut={ayarlar.riskYenilemePeriyotAy}
          yaz={yaz}
          etiket={etiket('risk_yenileme_periyot_ay')}
        />

        <RiskBantTablosu varsayilanMetodoloji={ayarlar.riskMetodolojisi} />
        {/* T10 — TERMİN ÖN-UYARISI (Kapsam §22.3/16). Boş = kapalı; gecikme
            bildirimi bu ayardan BAĞIMSIZ olarak sürer ve açıklama bunu
            açıkça söyler — kullanıcı ayarı kapatınca uyarısız kaldığını
            sanmasın. */}
        <Group align="flex-end" gap="xs">
          <NumberInput
            label={etiket('aksiyon_termin_on_uyari_gun')}
            description="Boş bırakılırsa ön-uyarı gönderilmez. Termin günü ve sonrasındaki gecikme uyarısı bu ayardan bağımsız olarak her gün gider."
            min={1}
            max={90}
            allowDecimal={false}
            value={terminOnUyari === '' ? (ayarlar.aksiyonTerminOnUyariGun ?? '') : terminOnUyari}
            onChange={setTerminOnUyari}
            data-testid="ayar-aksiyon_termin_on_uyari_gun"
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            loading={yaz.isPending}
            data-testid="ayar-aksiyon_termin_on_uyari_gun-kaydet"
            onClick={() => {
              const ham =
                terminOnUyari === '' ? ayarlar.aksiyonTerminOnUyariGun : Number(terminOnUyari);
              yaz.mutate(
                {
                  anahtar: 'aksiyon_termin_on_uyari_gun',
                  deger: ham === null || Number.isNaN(ham) ? null : ham,
                },
                { onSuccess: () => setTerminOnUyari('') },
              );
            }}
          >
            Kaydet
          </Button>
        </Group>
        <YazmaHatasi hata={yaz.error} />
      </Stack>
    </Card>
  );
}

/** Sunucunun 422/403 cümlesi olduğu gibi gösterilir — ekran metin uydurmaz. */
function YazmaHatasi({ hata }: { hata: Error | null }): React.JSX.Element | null {
  if (!hata) return null;
  return (
    <Text size="sm" c={ANLAM.engel} role="alert" data-testid="ayar-hata">
      {hata.message}
    </Text>
  );
}

/**
 * EKİPMAN KONTROL PERİYOTLARI (F4·S10·B — madde 91; Kapsam §16.5, ERD §8).
 *
 * **ÜÇ KATMAN, EKRANDA DA ÜÇ SÜTUN:**
 *   · **Önerilen** — ürün kütüphanesinin değeri. **SALT GÖSTERİM.**
 *   · **Holding** — müşterinin belirlediği değer (tipin sözlük kuralı).
 *   · **İşyeri** — farklılaşma satırları; boşsa holding değeri geçerli.
 *
 * ⚠️ **"ÖNERİLEN" SÜTUNU BİR KAPI DEĞİLDİR (91-3).** Holding ya da işyeri
 * değeri öneriden **uzun** olabilir ve ekran bunu **işaretlemez**: kırmızı
 * yok, "gevşetildi" rozeti yok, gerekçe alanı yok, uyarı yok. *Öneriden
 * sapmayı işaretlemek, önerinin bir TABAN olduğunu iddia etmektir* ve o
 * iddia bize ait değildir (Kapsam §19/7 sınır paragrafı — ilke MEVZUAT
 * kaynaklı kurallar içindir; müşterinin kendi yapılandırması ona tabi
 * değildir). **Buraya kıyas mantığı yazma.**
 *
 * ✏️ **REVİZE 26.08.2026 (F4·S10 ARTÇI — ürün sahibi):** *"İş ekipmanlarını
 * işyerleri kendileri yükler; yüklerken periyodu da belirlesinler. Holdingden
 * gitmesine gerek yok bu kuralın."* **"Holding geneli (ay)" alanı BU KARTTAN
 * KALKTI** — çözümleme iki adımdır: **işyeri satırı → kütüphane önerisi.**
 * ⚠️ Alanı geri koyan kod, nüshada artık BULUNMAYAN bir kuralı yazar ve
 * kullanıcı *"kaydettim ama hiçbir vade değişmedi"* der.
 */
function EkipmanPeriyotlari(): React.JSX.Element {
  const config = useEkipmanPeriyotConfig();
  return (
    <Card withBorder data-testid="ekipman-periyotlari">
      <Stack gap="sm">
        <Text fw={600}>Ekipman kontrol periyotları</Text>
        <Text size="sm" c="dimmed">
          Periyodik kontrolün süresi ekipman <b>tipinde</b> tanımlıdır; tek tek ekipman kaydında
          süre tutulmaz. Süreyi <b>işyeri belirler</b> — ilk ekipman yüklenirken sorulur, buradan da
          değiştirilebilir. İşyeri satırı yoksa ürün kütüphanesinin önerisi geçerlidir.
        </Text>
        <QueryStatus queries={[config]} />
        {config.isSuccess && <PeriyotTablosu config={config.data} />}
      </Stack>
    </Card>
  );
}

function PeriyotTablosu({ config }: { config: EkipmanPeriyotConfig }): React.JSX.Element {
  const [tipId, setTipId] = useState<string | null>(null);
  const secili = config.tipler.find((t) => t.tipId === tipId) ?? null;
  return (
    <Stack gap="sm">
      <Select
        label="Ekipman tipi"
        placeholder="Tip seçin"
        searchable
        data={config.tipler.map((t) => ({ value: t.tipId, label: t.ad }))}
        value={tipId}
        data-testid="periyot-tip-secimi"
        onChange={setTipId}
      />
      {secili && <TipPeriyodu key={secili.tipId} tip={secili} config={config} />}
    </Stack>
  );
}

function TipPeriyodu({
  tip,
  config,
}: {
  tip: EkipmanTipPeriyodu;
  config: EkipmanPeriyotConfig;
}): React.JSX.Element {
  const isyeriYaz = useEkipmanIsyeriPeriyoduYaz();
  const [isyeriId, setIsyeriId] = useState<string | null>(null);
  const [isyeriAy, setIsyeriAy] = useState<number | string>('');

  const satirlar = config.isyeriSatirlari.filter((s) => s.equipmentTipId === tip.tipId);

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" data-testid="periyot-onerilen">
        {tip.onerilenAy === null
          ? 'Bu tip için ürün kütüphanesinde önerilen bir süre yok — süresi, ilk ekipman yüklenirken işyerinde belirlenir.'
          : `Önerilen: ${tip.onerilenAy} ay. İşyeri için satır yoksa bu süre geçerlidir.`}
      </Text>

      <Text fw={600} size="sm">
        İşyeri süreleri
      </Text>
      {/* ⚠️ TOPLU ETKİ AÇIKÇA YAZILIR (artçı 1c): satır tek bir ekipmanın
          değil, o işyerindeki AYNI TİPTEKİ TÜM ekipmanların vadesini
          değiştirir — aynı işyerinde aynı tipe iki farklı süre olmaz. */}
      <Text size="xs" c="dimmed" data-testid="periyot-toplu-uyari">
        Bir işyerinin süresini değiştirmek, o işyerindeki <b>tüm {tip.ad}</b> ekipmanlarının
        vadesini değiştirir.
      </Text>
      {satirlar.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="periyot-isyeri-bos">
          Bu tipte süresi belirlenmiş işyeri yok — hepsi kütüphane önerisini kullanıyor.
        </Text>
      ) : (
        <Stack gap={4}>
          {satirlar.map((s) => (
            <Group key={s.workplaceId} justify="space-between" data-testid="periyot-isyeri-satiri">
              <Text size="sm">
                {s.workplaceAd}: <b>{s.kontrolPeriyoduAy} ay</b>
              </Text>
              <Button
                size="compact-sm"
                variant="subtle"
                color={ANLAM.engel}
                loading={isyeriYaz.isPending}
                data-testid="periyot-isyeri-kaldir"
                onClick={() =>
                  isyeriYaz.mutate({
                    workplaceId: s.workplaceId,
                    equipmentTipId: tip.tipId,
                    kontrolPeriyoduAy: null,
                  })
                }
              >
                Kaldır
              </Button>
            </Group>
          ))}
        </Stack>
      )}
      <Group align="flex-end" gap="xs">
        <Select
          label="İşyeri"
          placeholder="İşyeri seçin"
          searchable
          data={config.isyerleri.map((w) => ({ value: w.id, label: w.ad }))}
          value={isyeriId}
          data-testid="periyot-isyeri-secimi"
          onChange={setIsyeriId}
          style={{ flex: 1 }}
        />
        <NumberInput
          label="Süre (ay)"
          min={1}
          allowDecimal={false}
          value={isyeriAy}
          onChange={setIsyeriAy}
          data-testid="periyot-isyeri-ay"
          style={{ width: 120 }}
        />
        <Button
          variant="light"
          disabled={!isyeriId || !isyeriAy}
          loading={isyeriYaz.isPending}
          data-testid="periyot-isyeri-kaydet"
          onClick={() =>
            isyeriId &&
            isyeriYaz.mutate(
              {
                workplaceId: isyeriId,
                equipmentTipId: tip.tipId,
                kontrolPeriyoduAy: Number(isyeriAy),
              },
              {
                onSuccess: () => {
                  setIsyeriId(null);
                  setIsyeriAy('');
                },
              },
            )
          }
        >
          Kaydet
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * RİSK YENİLEME PERİYODU (tur 11 / R4-R5; Kapsam §16.12/8 · madde 129-132).
 *
 * ⚠️ **BU ALAN 17 GÜN BOYUNCA HİÇ YOKTU:** `riskYenilemePeriyotIhlalleri`
 * 19.08.2026'da yazıldı, testlendi ve **çağıranı olmadı** — *"müşteri
 * sıkılaştırabilir"* cümlesinin karşılığı üründe hiçbir yerde yoktu.
 *
 * ⚠️ **BOŞ = MEVZUAT TAVANI** (6/4/2 yıl) ve bu bir eksiklik değildir.
 * Tavanı aşan değer sunucuda **422** alır; ekran ikinci bir kural yazmaz —
 * mesajı sunucudan basar (D2).
 */
function RiskYenilemePeriyodu({
  mevcut,
  yaz,
  etiket,
}: {
  mevcut: TenantSettings['riskYenilemePeriyotAy'];
  yaz: ReturnType<typeof useTenantSettingYaz>;
  etiket: string;
}): React.JSX.Element {
  const [az, setAz] = useState<number | string>('');
  const [tehlikeli, setTehlikeli] = useState<number | string>('');
  const [cok, setCok] = useState<number | string>('');
  const deger = (girilen: number | string, varsayilan: number | undefined): number | string =>
    girilen === '' ? (varsayilan ?? '') : girilen;
  const sayi = (girilen: number | string, varsayilan: number | undefined): number =>
    girilen === '' ? (varsayilan ?? 0) : Number(girilen);

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        {etiket}
      </Text>
      <Text size="sm" c="dimmed" data-testid="risk-yenileme-notu">
        Boş bırakılırsa mevzuat tavanı geçerlidir: az tehlikeli 72 ay · tehlikeli 48 ay · çok
        tehlikeli 24 ay. Bu süreler <b>kısaltılabilir, uzatılamaz</b>.
      </Text>
      <Group align="flex-end" gap="xs">
        <NumberInput
          label="Az tehlikeli"
          min={1}
          allowDecimal={false}
          value={deger(az, mevcut?.az)}
          onChange={setAz}
          data-testid="risk-yenileme-az"
        />
        <NumberInput
          label="Tehlikeli"
          min={1}
          allowDecimal={false}
          value={deger(tehlikeli, mevcut?.tehlikeli)}
          onChange={setTehlikeli}
          data-testid="risk-yenileme-tehlikeli"
        />
        <NumberInput
          label="Çok tehlikeli"
          min={1}
          allowDecimal={false}
          value={deger(cok, mevcut?.cok)}
          onChange={setCok}
          data-testid="risk-yenileme-cok"
        />
        <Button
          variant="light"
          loading={yaz.isPending}
          data-testid="risk-yenileme-kaydet"
          onClick={() =>
            yaz.mutate({
              anahtar: 'risk_yenileme_periyot_ay',
              deger: {
                az: sayi(az, mevcut?.az),
                tehlikeli: sayi(tehlikeli, mevcut?.tehlikeli),
                cok: sayi(cok, mevcut?.cok),
              },
            })
          }
        >
          Kaydet
        </Button>
        <Button
          variant="default"
          data-testid="risk-yenileme-sifirla"
          onClick={() => {
            setAz('');
            setTehlikeli('');
            setCok('');
            yaz.mutate({ anahtar: 'risk_yenileme_periyot_ay', deger: null });
          }}
        >
          Mevzuat tavanına dön
        </Button>
      </Group>
      {yaz.error && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="risk-yenileme-hata">
          {yaz.error.message}
        </Text>
      )}
    </Stack>
  );
}

/**
 * RİSK BANT TABLOSU (tur 11 / R2).
 *
 * ⚠️ **`PUT /api/risk-bands` F3B·S9'DAN BERİ VARDI ve ÇAĞIRANI YOKTU**
 * (h-3 sınıfı). R2, DÖF öneri eşiğini bu tablodan türettiği için tablo
 * **düzenlenebilir olmak zorunda** — aksi hâlde müşterinin elinden bir
 * ayar alınmış, yerine hiçbir şey konmamış olurdu.
 *
 * ⚠️ **YÖNTEM SEÇİLİR:** R1'den sonra aynı holdingde iki yöntem yan yana
 * yaşayabilir; her yöntemin **kendi bant tablosu** vardır.
 * ⚠️ Bütünlük kuralı (dört kademe · ayrık aralık · yalnız en üst bant açık
 * uçlu) **sunucudadır** (`riskBantIhlalleri`) ve 422 verir; ekran ikinci bir
 * doğrulama yazmaz.
 */
function RiskBantTablosu({
  varsayilanMetodoloji,
}: {
  varsayilanMetodoloji: TenantSettings['riskMetodolojisi'];
}): React.JSX.Element {
  const [metodoloji, setMetodoloji] = useState<string>(varsayilanMetodoloji);
  const tablo = useRiskBantTablosu(metodoloji);
  const yaz = useRiskBantYaz();
  const [taslak, setTaslak] = useState<Record<string, { alt: string; ust: string }> | null>(null);

  const satirlar =
    taslak ??
    Object.fromEntries(
      RISK_SEVIYELERI.map((seviye) => {
        const bant = tablo.data?.bantlar.find((b) => b.seviye === seviye);
        return [
          seviye,
          {
            alt: bant ? String(bant.altSinir) : '',
            ust: bant?.ustSinir === null || bant === undefined ? '' : String(bant.ustSinir),
          },
        ];
      }),
    );

  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Risk bant tablosu
      </Text>
      <Text size="sm" c="dimmed">
        Ham skoru dört kademeye eşler. Sınırlar dâhildir;{' '}
        <b>en üst bandın üst sınırı boş bırakılır</b> (sonsuz). Değişiklik, o yöntemdeki{' '}
        <b>tüm maddelerin</b> seviyesini yeniden hesaplar.
      </Text>
      <Select
        label="Yöntem"
        data={RISK_METODOLOJILERI.map((m) => ({ value: m, label: RISK_METODOLOJI_ETIKETI[m] }))}
        value={metodoloji}
        onChange={(v) => {
          if (!v) return;
          setMetodoloji(v);
          setTaslak(null);
        }}
        allowDeselect={false}
        data-testid="risk-bant-metodoloji"
      />
      {RISK_SEVIYELERI.map((seviye) => (
        <Group key={seviye} align="flex-end" gap="xs">
          <Text size="sm" w={160} data-testid={`risk-bant-${seviye}`}>
            {RISK_SEVIYESI_ETIKETI[seviye]}
          </Text>
          <NumberInput
            label="Alt sınır"
            value={satirlar[seviye]?.alt ?? ''}
            onChange={(v) =>
              setTaslak({
                ...satirlar,
                [seviye]: { alt: String(v), ust: satirlar[seviye]?.ust ?? '' },
              })
            }
            data-testid={`risk-bant-${seviye}-alt`}
          />
          <NumberInput
            label="Üst sınır"
            value={satirlar[seviye]?.ust ?? ''}
            onChange={(v) =>
              setTaslak({
                ...satirlar,
                [seviye]: { alt: satirlar[seviye]?.alt ?? '', ust: String(v) },
              })
            }
            data-testid={`risk-bant-${seviye}-ust`}
          />
        </Group>
      ))}
      <Group>
        <Button
          variant="light"
          loading={yaz.isPending}
          data-testid="risk-bant-kaydet"
          onClick={() =>
            yaz.mutate(
              {
                metodoloji: metodoloji as (typeof RISK_METODOLOJILERI)[number],
                bantlar: RISK_SEVIYELERI.map((seviye) => ({
                  seviye,
                  altSinir: Number(satirlar[seviye]?.alt ?? 0),
                  ustSinir:
                    (satirlar[seviye]?.ust ?? '') === ''
                      ? null
                      : Number(satirlar[seviye]?.ust ?? 0),
                })),
              },
              { onSuccess: () => setTaslak(null) },
            )
          }
        >
          Bantları kaydet
        </Button>
      </Group>
      {yaz.error && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="risk-bant-hata">
          {yaz.error.message}
        </Text>
      )}
    </Stack>
  );
}
